// src/components/OperatorRedirect.jsx

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataLayer } from '@quantiix/data-layer';
import { createLogger } from '../utils/logger';
import { resolveLandingScope } from '../config/landing';

const logger = createLogger('Shell::OperatorRedirect');

export const OperatorRedirect = ({ children }) => {
    const { isAuthenticated, permissions, loading, isSuperuser, hasAssignedCompany } = useAuth();
    const { setScope } = useDataLayer();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (loading) return;
        if (!isAuthenticated) return;

        // Only redirect from home page (post-login landing)
        if (location.pathname !== '/') return;

        const isOperator = permissions.roles?.includes('operator');
        const assignedMachine = permissions.machines?.[0];
        const assignedCompany = permissions.company?.[0];
        const assignedFactory = permissions.factories?.[0];

        if (isOperator && assignedMachine && assignedCompany) {
            // Extract IDs from objects (permissions may contain objects or strings)
            const companyId = typeof assignedCompany === 'object' ? assignedCompany.id : assignedCompany;
            const factoryId = typeof assignedFactory === 'object' ? assignedFactory.id : assignedFactory;

            logger.info('Operator detected, redirecting to operator view', {
                machineId: assignedMachine,
                companyId,
                factoryId
            });

            // Set context before navigating
            setScope({
                companyId,
                factoryId,
                machineId: assignedMachine
            });

            navigate(`/app/operator/${assignedMachine}`, { replace: true });
            return;
        }

        // Not an operator. Land on the narrowest level this user's scope actually names:
        // "empty means all", so a level naming exactly one asset describes someone who
        // only ever sees that asset, and the company map makes them drill a hierarchy
        // they do not have (monday 12943481904). See config/landing.js for the rule.
        const landing = resolveLandingScope({ permissions, isSuperuser, hasAssignedCompany });
        if (!landing) return;

        const companyId = typeof assignedCompany === 'object' ? assignedCompany.id : assignedCompany;

        logger.info('Scoped user detected, redirecting to their own level', {
            level: landing.level,
            id: landing.id,
            companyId
        });

        // setScope MERGES, so send only the levels this landing actually establishes.
        //
        // The machine branch deliberately does NOT seed factoryId. permissions.factories[0]
        // is not authoritative — only a level naming exactly ONE asset is, and a user may
        // legitimately hold several factories alongside a single machine, which would seed
        // the WRONG factory. Worse, any factoryId at all makes the Machine MFE skip its own
        // resolution: `needsResolve = companyId && machineId && !factoryId` (machine
        // App.jsx:218), which is what fills in factory + OU from the hierarchy AND what
        // arms its not-found/no-access fallback. Leaving it unset is the same choice
        // MachineContextSeed.jsx:48 makes for deep links, and it lets that resolution
        // overwrite any stale ouId left in the session scope.
        //
        // The factory branch needs no such care: the Dashboard reconciles the scope to its
        // route level on arrival (dashboard App.jsx:116-124), clearing ouId and machineId.
        setScope({
            ...(companyId ? { companyId } : {}),
            ...(landing.level === 'machine'
                ? { machineId: landing.id }
                : { factoryId: landing.id })
        });

        navigate(landing.path, { replace: true });
    }, [isAuthenticated, permissions, loading, isSuperuser, hasAssignedCompany,
        location.pathname, navigate, setScope]);

    return children;
};